// Global Instructions Rule Applied!
//
// Configuration → Deal Origination: which deal types are barred from the Deal
// Origination pipeline (cfg.pipeline_deal_type_exclusions).
//
// An excluded type does two things at once, because both the creation flow and
// the follower membership predicate read the same cfg table:
//   * a new deal of that type never joins the origination board (the New Deal
//     drawer hides the Pipeline Stage field entirely), and
//   * it never gets a Deal Origination follower.
// Execution is untouched — a Disposal deal still enters its execution pipeline
// and is still followed there.
//
// The toggle governs what happens NEXT. Existing deals keep their board rows
// and their followers; retiring those was a one-off cleanup migration
// (20261014120300). The confirm dialog says so whenever the change would
// otherwise look retroactive.
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Card, Modal, Switch, Table } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { DealOriginationExclusionRow } from '../../../state_manager/data_models/deal_origination_admin_model.ts'
import {
  fetchDealOriginationExclusions,
  setDealOriginationExclusion
} from '../services/dealOriginationConfigService.ts'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

export default function DealOriginationPanel(): React.ReactElement {
  const token = () => getAccessToken(store.getState().auth)
  // Modal.useModal, not the static Modal.confirm: under React 19 the static
  // function is CALLED but renders nothing (antd v5 supports React 16-18 and
  // warns "Static function can not consume context"). With the static call the
  // confirm-gated toggle did nothing at all — the switch simply refused to
  // move for exactly the deal types that have deals on the board.
  const [modal, modalContextHolder] = Modal.useModal()

  const [rows, setRows] = useState<DealOriginationExclusionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [savingId, setSavingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchDealOriginationExclusions(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load deal origination settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const applyChange = async (row: DealOriginationExclusionRow, excluded: boolean): Promise<void> => {
    setSavingId(row.deal_type_id)
    try {
      await setDealOriginationExclusion(token(), row.deal_type_id, excluded)
      toast.success(
        excluded
          ? `${row.deal_type__label || 'Deal type'} excluded from Deal Origination`
          : `${row.deal_type__label || 'Deal type'} re-admitted to Deal Origination`
      )
      await load()
      // The exclusions ride global configs (the New Deal drawer reads them), so
      // pull the refreshed cache into Redux rather than waiting for a restart.
      refreshBoardConfigs()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to update setting')
      // A refused write leaves a controlled Switch lying — reload the truth.
      await load()
    } finally {
      setSavingId(null)
    }
  }

  const handleToggle = (row: DealOriginationExclusionRow, excluded: boolean): void => {
    const liveCount = row.origination_deals_count ?? 0
    if (excluded && liveCount > 0) {
      modal.confirm({
        title: `Exclude ${row.deal_type__label || 'this deal type'} from Deal Origination?`,
        content: (
          <div className='space-y-2'>
            <div>
              New deals of this type will skip the origination pipeline and will not get a Deal
              Origination follower.
            </div>
            <div>
              {liveCount} existing deal{liveCount === 1 ? '' : 's'} of this type{' '}
              {liveCount === 1 ? 'is' : 'are'} already on the origination board. Existing deals are
              not changed by this setting — they keep their board position and their follower.
            </div>
          </div>
        ),
        okText: 'Exclude',
        cancelText: 'Cancel',
        onOk: () => applyChange(row, true)
      })
      return
    }
    applyChange(row, excluded)
  }

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      {modalContextHolder}
      <Card
        size='small'
        title='Deal types in Deal Origination'
        extra={
          <span className='text-xs text-gray-400'>
            Excluded types skip the origination board and its follower agent
          </span>
        }
      >
        <Alert
          type='info'
          showIcon
          className='mb-3'
          message='This setting governs newly created deals.'
          description='Excluding a type stops new deals of that type entering Deal Origination and stops the Deal Origination follower being provisioned for them. Deals already on the board are left as they are. Deal Execution is unaffected — an excluded type still enters and is still followed in its execution pipeline.'
        />
        <Table
          size='small'
          rowKey='deal_type_id'
          loading={loading}
          pagination={false}
          dataSource={rows}
          columns={[
            {
              title: 'Deal type',
              dataIndex: 'deal_type__label',
              render: (v) => v || '—'
            },
            {
              title: 'On origination board',
              dataIndex: 'origination_deals_count',
              align: 'right' as const,
              width: 180,
              render: (v) => (v ?? 0).toLocaleString()
            },
            {
              title: 'Excluded from origination',
              key: 'excluded',
              width: 220,
              render: (_, row) => (
                <Switch
                  checked={row.excluded}
                  loading={savingId === row.deal_type_id}
                  onChange={(checked) => handleToggle(row, checked)}
                />
              )
            }
          ]}
        />
      </Card>
    </div>
  )
}
